import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatMoney, formatDateShort, formatTime } from "@/lib/format";
import { PAYMENT_METHODS } from "@/lib/bookingConstants";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Loader2, Eye, Ban } from "lucide-react";

const AUDIT_LABELS = {
  "expense.created": "Dépense créée", "expense.updated": "Dépense modifiée",
  "expense.cancelled": "Dépense annulée", "expense.outside_cash_session": "Enregistrée hors caisse",
};

export default function ExpenseDetailsDrawer({ expenseId, open, onOpenChange }) {
  const { orgId, can } = useAuth();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");

  const { data: e, isLoading } = useQuery({
    queryKey: ["expense", orgId, expenseId],
    queryFn: async () => (await api.get(`/api/expenses/${expenseId}`, { params: { organization_id: orgId } })).data,
    enabled: open && !!expenseId,
  });
  const currency = e?.currency || "MAD";

  const cancel = useMutation({
    mutationFn: async () => (await api.post(`/api/expenses/${expenseId}/cancel`, { cancellation_reason: reason }, { params: { organization_id: orgId } })).data,
    onSuccess: () => { toast.success("Dépense annulée"); qc.invalidateQueries(); setConfirming(false); setReason(""); onOpenChange(false); },
    onError: (er) => toast.error(formatApiError(er.response?.data?.detail) || er.message),
  });

  const viewReceipt = async () => {
    try {
      const { data } = await api.get(`/api/files/${e.receipt_file_id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank", "noopener");
    } catch { toast.error("Impossible d'ouvrir le justificatif"); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" aria-describedby={undefined} className="w-full overflow-y-auto p-0 sm:max-w-md" data-testid="expense-details-drawer">
        {isLoading || !e ? (
          <div className="flex h-full items-center justify-center"><SheetTitle className="sr-only">Chargement</SheetTitle><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <>
            <SheetHeader className="border-b border-slate-100 px-6 py-4">
              <div className="flex items-center justify-between">
                <SheetTitle className="font-mono">{e.expense_reference}</SheetTitle>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${e.status === "CANCELLED" ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"}`} data-testid="expense-status-badge">
                  {e.status === "CANCELLED" ? "Annulée" : "Valide"}
                </span>
              </div>
              <SheetDescription className="sr-only">Détail de la dépense {e.expense_reference}.</SheetDescription>
            </SheetHeader>
            <div className="space-y-4 p-6">
              <div className="rounded-xl bg-slate-50 p-4 text-center">
                <p className="text-xs text-slate-400">Montant</p>
                <p className="font-mono text-2xl font-bold text-slate-900" data-testid="expense-amount">{formatMoney(e.amount_cents, currency)}</p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info k="Catégorie" v={e.category_name} />
                <Info k="Mode de paiement" v={PAYMENT_METHODS[e.payment_method] || e.payment_method} />
                <Info k="Date" v={formatDateShort(e.expense_date)} />
                <Info k="Bénéficiaire" v={e.supplier_or_recipient || "—"} />
                <Info k="Saisi par" v={e.created_by_name} />
                <Info k="Caisse" v={e.cash_session_id ? "Rattachée" : (e.outside_cash_session ? "Hors session" : "—")} />
              </div>
              {e.description && <div><p className="text-xs text-slate-400">Description</p><p className="text-sm text-slate-700">{e.description}</p></div>}
              {e.receipt_file_id && (
                <button onClick={viewReceipt} data-testid="expense-view-receipt" className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-primary hover:bg-emerald-50">
                  <Eye className="h-4 w-4" /> Voir le justificatif
                </button>
              )}
              {e.status === "CANCELLED" && (
                <div className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700" data-testid="expense-cancel-reason">Annulée — {e.cancellation_reason}</div>
              )}
              <div>
                <p className="mb-2 text-sm font-semibold text-slate-700">Historique</p>
                <div className="space-y-1">
                  {(e.history || []).map((h, i) => (
                    <div key={i} className="flex items-center justify-between text-xs text-slate-500" data-testid={`expense-history-${i}`}>
                      <span>{AUDIT_LABELS[h.action] || h.action}{h.actor_name ? ` · ${h.actor_name}` : ""}</span>
                      <span>{formatDateShort(h.created_at)} {formatTime(h.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
              {e.status !== "CANCELLED" && can("expenses.manage") && (
                confirming ? (
                  <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50 p-3">
                    <input value={reason} onChange={(ev) => setReason(ev.target.value)} placeholder="Motif d'annulation" data-testid="cancel-reason-input"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                    <div className="flex gap-2">
                      <button onClick={() => cancel.mutate()} disabled={!reason.trim() || cancel.isPending} data-testid="confirm-cancel-expense-button"
                        className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50">
                        {cancel.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Confirmer l'annulation
                      </button>
                      <button onClick={() => setConfirming(false)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600">Retour</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setConfirming(true)} data-testid="cancel-expense-button"
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-rose-200 px-4 py-2.5 text-sm font-semibold text-rose-600 hover:bg-rose-50">
                    <Ban className="h-4 w-4" /> Annuler la dépense
                  </button>
                )
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

const Info = ({ k, v }) => (<div><p className="text-xs text-slate-400">{k}</p><p className="font-medium text-slate-800">{v}</p></div>);
